import { zodResolver } from '@hookform/resolvers/zod';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Slack, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const INTEGRATION_NAME = 'slackv2' as const;
const QUERY_KEY = [QueryKey.integration.get, INTEGRATION_NAME] as const;

export interface SlackV2Params {
  slackv2_bot_token?: string;
  slackv2_bot_user_id?: string;
  slackv2_team_id?: string;
  slackv2_team_name?: string;
  slackv2_bot_active?: boolean;
  slackv2_reply_thread?: boolean;
  slackv2_reply_channel?: boolean;
}

interface IntegrationResponse {
  name: string;
  params: SlackV2Params;
}

const formSchema = z.object({
  slackv2_bot_active: z.boolean(),
  slackv2_reply_thread: z.boolean(),
  slackv2_reply_channel: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

export interface SlackV2SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SlackV2Sheet = ({ open, onOpenChange }: SlackV2SheetProps) => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);

  const { data } = useQuery<IntegrationResponse>({
    queryKey: QUERY_KEY,
    enabled: open,
  });

  const params = data?.params ?? {};
  const isConnected = Boolean(params.slackv2_bot_token);
  const teamName = params.slackv2_team_name ?? '';

  const defaultValues = useMemo<FormValues>(
    () => ({
      slackv2_bot_active: Boolean(params.slackv2_bot_active),
      slackv2_reply_thread: Boolean(params.slackv2_reply_thread),
      slackv2_reply_channel: Boolean(params.slackv2_reply_channel),
    }),
    [params.slackv2_bot_active, params.slackv2_reply_thread, params.slackv2_reply_channel],
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Re-sync form when server data arrives / changes. `reset` clears dirty flags
  // so only user-initiated edits mark fields as changed.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!open) {
      setConfirmDisconnectOpen(false);
    }
  }, [open]);

  const botActive = watch('slackv2_bot_active');

  const oauthMutation = useMutation<{ url: string }>({
    mutationKey: [QueryKey.integration.slackV2Oauth],
    onSuccess: (result) => {
      if (!result?.url) {
        toast.error(t('integration:slackV2.oauth_failed'));
        return;
      }
      window.location.href = result.url;
    },
    onError: () => {
      toast.error(t('integration:slackV2.oauth_failed'));
    },
  });

  const patchMutation = useMutation<
    IntegrationResponse,
    AxiosError,
    { name: typeof INTEGRATION_NAME; params: Partial<FormValues> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData<IntegrationResponse>(QUERY_KEY, response);
      reset({
        slackv2_bot_active: Boolean(response.params.slackv2_bot_active),
        slackv2_reply_thread: Boolean(response.params.slackv2_reply_thread),
        slackv2_reply_channel: Boolean(response.params.slackv2_reply_channel),
      });
      toast.success(t('integration:slackV2.saved'));
    },
    onError: () => {
      toast.error(t('integration:slackV2.save_failed'));
    },
  });

  const revokeMutation = useMutation({
    mutationKey: [QueryKey.integration.slackV2Revoke],
    onSuccess: () => {
      setConfirmDisconnectOpen(false);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      toast.error(t('integration:slackV2.revoke_failed'));
    },
  });

  const onSubmit = (values: FormValues) => {
    const changed: Partial<FormValues> = {};
    (Object.keys(dirtyFields) as (keyof FormValues)[]).forEach((key) => {
      if (dirtyFields[key]) {
        changed[key] = values[key];
      }
    });
    if (Object.keys(changed).length === 0) return;
    patchMutation.mutate({ name: INTEGRATION_NAME, params: changed });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" data-testid="slackv2-sheet">
        <SheetHeader className="px-0.5">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-muted p-2 flex items-center justify-center">
              <Slack className="size-5 text-foreground" />
            </div>
            <div>
              <SheetTitle data-testid="slackv2-sheet-title">
                {t('integration:slackV2.title')}
              </SheetTitle>
              <p className="text-sm text-muted-foreground m-0 mt-0.5">
                {t('integration:slackV2.description')}
              </p>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="slackv2-close-button"
            aria-label="Close"
          >
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <Separator />

        <div className="overflow-y-auto px-0.5 space-y-6 flex-1">
          {!isConnected ? (
            <div
              className="flex flex-col items-center justify-center gap-4 py-12 text-center"
              data-testid="slackv2-not-connected"
            >
              <p className="text-sm text-muted-foreground max-w-sm">
                {t('integration:slackV2.description')}
              </p>
              <Button
                variant="dark"
                size="md"
                onClick={() => {
                  capture(PostHogEventEnum.SlackIntegrationAuthenticateClicked);
                  oauthMutation.mutate();
                }}
                disabled={oauthMutation.isPending}
                data-testid="slackv2-connect-button"
              >
                <Slack className="size-4" />
                {oauthMutation.isPending
                  ? t('integration:slackV2.connecting')
                  : t('integration:slackV2.connect')}
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6"
              data-testid="slackv2-settings-form"
            >
              <div
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-muted p-4"
                data-testid="slackv2-connected-banner"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Badge variant="success" size="sm" data-testid="slackv2-team-badge">
                    {t('integration:slackV2.connected_to', { team: teamName })}
                  </Badge>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmDisconnectOpen(true)}
                  disabled={revokeMutation.isPending}
                  data-testid="slackv2-disconnect-button"
                >
                  {t('integration:slackV2.disconnect')}
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <Label
                    htmlFor="slackv2-bot-active"
                    className="text-sm font-medium text-foreground"
                  >
                    {t('integration:slackV2.active.label')}
                  </Label>
                  <Controller
                    control={control}
                    name="slackv2_bot_active"
                    render={({ field }) => (
                      <Switch
                        id="slackv2-bot-active"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="slackv2-bot-active-toggle"
                      />
                    )}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <Label
                    htmlFor="slackv2-reply-thread"
                    className="text-sm font-medium text-foreground data-[disabled]:text-foreground-subtle"
                    data-disabled={!botActive || undefined}
                  >
                    {t('integration:slackV2.reply_thread.label')}
                  </Label>
                  <Controller
                    control={control}
                    name="slackv2_reply_thread"
                    render={({ field }) => (
                      <Switch
                        id="slackv2-reply-thread"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!botActive}
                        data-testid="slackv2-reply-thread-toggle"
                      />
                    )}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <Label
                    htmlFor="slackv2-reply-channel"
                    className="text-sm font-medium text-foreground data-[disabled]:text-foreground-subtle"
                    data-disabled={!botActive || undefined}
                  >
                    {t('integration:slackV2.reply_channel.label')}
                  </Label>
                  <Controller
                    control={control}
                    name="slackv2_reply_channel"
                    render={({ field }) => (
                      <Switch
                        id="slackv2-reply-channel"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!botActive}
                        data-testid="slackv2-reply-channel-toggle"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="dark"
                  size="md"
                  disabled={!isDirty || patchMutation.isPending}
                  data-testid="slackv2-save-button"
                >
                  {t('integration:slackV2.save')}
                </Button>
              </div>
            </form>
          )}
        </div>

        <AlertDialogPrimitive.Root
          open={confirmDisconnectOpen}
          onOpenChange={setConfirmDisconnectOpen}
        >
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 z-20" />
          <AlertDialogPrimitive.Content
            className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
            data-testid="slackv2-disconnect-dialog"
          >
            <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-lg space-y-4">
              <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                {t('integration:slackV2.disconnect_confirm.title')}
              </AlertDialogPrimitive.Title>
              <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('integration:slackV2.disconnect_confirm.description')}
              </AlertDialogPrimitive.Description>
              <div className="flex items-center justify-end gap-2 pt-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="slackv2-disconnect-cancel">
                    {t('integration:slackV2.disconnect_confirm.cancel')}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => revokeMutation.mutate()}
                  disabled={revokeMutation.isPending}
                  data-testid="slackv2-disconnect-confirm"
                >
                  {t('integration:slackV2.disconnect_confirm.confirm')}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};

export default SlackV2Sheet;
